import { useState } from 'react';
import {
  Box,
  ButtonBase,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ABOUT_TO_START } from '../steps';

// Test box selector for the new dashboard's top bar, replacing the classic
// view's box column. Status colours match the classic list: blue writing,
// green online, grey offline.
export function boxStatus(box) {
  if (box?.about_to_start) return ABOUT_TO_START;
  if (box?.online && box?.writing) return { color: '#0ea5e9', label: 'Writing' };
  if (box?.online) return { color: '#048904', label: 'Online' };
  return { color: '#9e9e9e', label: 'Offline' };
}

function lastReadingLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function Dot({ color }) {
  return <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />;
}

function BoxPicker({ boxes, selected, onSelect }) {
  const [anchor, setAnchor] = useState(null);
  const status = boxStatus(selected);
  const last = lastReadingLabel(selected?.LastReadingDate);
  const subtitle = [selected?.Logger, status.label + (!selected?.online && last ? `, last reading ${last}` : '')]
    .filter(Boolean)
    .join(' · ');

  return (
    <Paper sx={{ display: 'flex', minWidth: 0, flex: { xs: '1 1 100%', md: '0 1 340px' }, borderRadius: 1 }}>
      <ButtonBase
        aria-haspopup='listbox'
        aria-expanded={Boolean(anchor)}
        aria-label='Choose test box'
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ flex: 1, minWidth: 0, minHeight: 56, px: 1.5, py: 0.75, gap: 1.25, justifyContent: 'flex-start', borderRadius: 1, textAlign: 'left' }}
      >
        <Dot color={status.color} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='body2' fontWeight={600} noWrap>{selected?.Name || 'Select a test box'}</Typography>
          <Typography variant='caption' color='text.secondary' noWrap component='div'>{subtitle || selected?.JobNumber || '—'}</Typography>
        </Box>
        <ExpandMoreIcon fontSize='small' />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        disableScrollLock
        PaperProps={{ sx: { maxHeight: 420, minWidth: 300 } }}
      >
        {boxes.map((box) => {
          const s = boxStatus(box);
          return (
            <MenuItem
              key={box.id}
              selected={box.id === selected?.id}
              onClick={() => { setAnchor(null); onSelect(box); }}
              sx={{ gap: 1.25 }}
            >
              <Dot color={s.color} />
              <ListItemText
                primary={box.Name}
                secondary={[box.Logger, box.JobNumber || s.label].filter(Boolean).join(' · ')}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Paper>
  );
}

export default BoxPicker;
